import Image from 'next/image';

import { Box, Button, Flex, Heading, Text } from '@chakra-ui/react';

import { ClaudeEngineerNoLogoTag } from '~/features/claude-engineer-2026/components/claude-engineer-no-logo-tag';
import { ClaudeLogo } from '~/features/claude-engineer-2026/components/claude-logo';
import { CLAUDE_ENGINEER_ASSETS, COURSE_PATH, FLAG_VARIANT_LOGO } from '~/features/claude-engineer-2026/constants';
import type { ClaudeEngineerVariant } from '~/features/claude-engineer-2026/models/variant';
import { PromoCard } from '~/features/experiments/ab_plp_redesign_2025/components/promo-card';
import { RocketIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export function ClaudeEngineerExperiencePromoCard({
  onClick,
  variant,
}: {
  onClick?: () => void;
  variant: ClaudeEngineerVariant;
}) {
  const showLogo = variant === FLAG_VARIANT_LOGO;

  return (
    <PromoCard
      backgroundColor='black'
      borderRadius='8px'
      id='claude-engineer-title'
      minHeight='328.57px'
      sectionGap={4}
      backgroundImageSlot={
        <>
          <Box bottom={0} display={{ base: 'block', md: 'none' }} left={0} position='absolute' right={0} top={0}>
            <Image
              alt=''
              quality={90}
              sizes='100vw'
              src={CLAUDE_ENGINEER_ASSETS.experienceBannerBgMobile}
              style={{
                objectFit: 'cover',
                objectPosition: 'top',
              }}
              fill
            />
          </Box>
          <Box
            bottom={0}
            display={{ base: 'none', md: 'block' }}
            overflow='hidden'
            position='absolute'
            right={0}
            top={0}
            width='72%'
          >
            <Image
              alt=''
              quality={100}
              sizes='72vw'
              src={CLAUDE_ENGINEER_ASSETS.experienceBannerBg}
              style={{
                mixBlendMode: 'screen',
                objectFit: 'cover',
                objectPosition: 'right center',
              }}
              fill
            />
          </Box>
          <Box
            bgGradient='linear(90deg, rgba(0, 0, 0, 0.96) 0%, rgba(0, 0, 0, 0.88) 42%, rgba(0, 0, 0, 0.55) 72%, rgba(0, 0, 0, 0.12) 100%)'
            display={{ base: 'none', md: 'block' }}
            inset={0}
            pointerEvents='none'
            position='absolute'
          />
        </>
      }
      buttonSlot={
        <Button
          colorScheme='blue'
          leftIcon={<RocketIcon height='18px' width='18px' />}
          position='relative'
          size='icon-left'
          variant='solid'
          zIndex={1}
          width={{
            base: '100%',
            md: '149px',
          }}
        >
          Enroll Now
        </Button>
      }
      contentSlot={
        <Box maxWidth={{ base: 'unset', md: '1008px' }} position='relative' zIndex={1}>
          <Heading
            as='h2'
            color='white'
            fontWeight='600'
            id='claude-engineer-title'
            letterSpacing='-0.36px'
            mb={3}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
            lineHeight={{
              base: '1.14',
              md: '1.1',
            }}
          >
            AI Engineering with Claude
          </Heading>
          <Text color='orange.200'>Ship 13 real projects with Claude, get feedback from experienced mentors.</Text>
        </Box>
      }
      eyebrowSlot={
        <Flex
          alignItems='center'
          color='orange.200'
          gap={4}
          position='relative'
          zIndex={1}
          _after={{
            bgGradient: 'linear(90deg, #FFDCC3 0%, rgba(255, 220, 195, 0.2) 100%)',
            content: '""',
            display: 'block',
            height: '0.5px',
            width: '100%',
          }}
        >
          {showLogo ? <ClaudeLogo size='experience' /> : <ClaudeEngineerNoLogoTag size='experience' />}
        </Flex>
      }
      linkOverlay={{
        ariaLabel: 'AI Engineering with Claude: Enroll Now',
        href: COURSE_PATH,
        onClick,
      }}
      padding={{
        base: '32px',
        md: '48px',
      }}
    />
  );
}
